/**
 * `BarSeries` — one vertical bar per year (or per practice group), with an optional mean
 * line across the plot. UC2's card charts: cropland share by year, rainfall by year with
 * the 2017–2025 average, practice adoption against the programme mean (client §3.1).
 *
 * Hand-built SVG in the same register as the other charts here (the repo forbids chart
 * libraries): viewBox-scaled, `role="img"` with a full plain-text readout carrying every
 * bar with its own label and unit, so the chart is readable by a screen reader and
 * assertable by a test without measuring pixels.
 *
 * Pure and translation-free, like `DimensionBars`/`PairedBars`: every string — the aria
 * label, the mean's name, the unit — is passed in already localised.
 */
import { useRef } from 'react';

import { fmtNum } from '../../../lib/format';
import { AxisLabels } from '../../../components/charts/AxisLabels';
import { useAxisTitles } from '../../../components/charts/axisContext';
import { axisBands } from '../../../components/charts/scale';
import { VALUE_HALO } from '../../../components/charts/labels';
import { useChartMarks } from '../../../components/charts/useChartMarks';
import { COMPACT_W, ROW_LABEL_PX, labelStride, showsLabel } from './labels';
import { useIntelWidth } from './layout';

/**
 * The width the chart is laid out at where its box cannot be measured — the Analysis dock's
 * own body width. In a browser it lays out at its measured width in real pixels (#749).
 */
export const BS_W = 394;
/** Plot height, above the year labels. Constant at every width. */
export const BS_PLOT_H = 72;
/** Room under the plot for the category labels. */
export const BS_LABEL_H = 16;
export const BS_PAD_TOP = 8;
export const BS_PAD_X = 2;
export const BS_H = BS_PAD_TOP + BS_PLOT_H + BS_LABEL_H;

const BAR_COLOR = '#2f6d4f';
const MEAN_COLOR = 'var(--ink-3)';

export interface BarSeriesBar {
  /** Stable key and `data-bar` value — the year, or the practice group id. */
  id: string;
  /** What is printed under the bar: a year, or a short group name. */
  label: string;
  value: number | null;
  /** The bar the card is about (this watershed's own group) — drawn solid, others faded. */
  highlight?: boolean;
  color?: string;
}

export interface BarSeriesProps {
  bars: BarSeriesBar[];
  ariaLabel: string;
  /** Drawn as a dashed line across the plot, and named in the readout. */
  mean?: number | null;
  meanLabel?: string;
  unit?: string;
  decimals?: number;
  /** `data-testid` of the `<svg>`, so a card with three charts can address each one. */
  testId?: string;
  /** The x axis' title, "quantity (unit)", centred under the chart (launch round). */
  xLabel?: string;
  /** The y axis' title, rotated at the left of the label column. */
  yLabel?: string;
  /** Layout width in px; measured from the chart's own box when omitted (#749). */
  w?: number;
}

/** One bar's own text — shared by the readout and the `<title>`, so they cannot disagree. */
function valueText(value: number | null, unit: string, decimals: number): string {
  return value == null ? '-' : `${fmtNum(value, decimals)}${unit ? ` ${unit}` : ''}`;
}

export function BarSeries({
  bars,
  ariaLabel,
  mean = null,
  meanLabel,
  unit = '',
  decimals = 1,
  testId = 'bar-series-chart',
  xLabel: xLabelProp,
  yLabel: yLabelProp,
  w: wProp,
}: BarSeriesProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  /* The axis titles' bands come out of the measured width: the plot is what is left. */
  const bands = axisBands(xLabel, yLabel);
  const outerW = useIntelWidth(svgRef, wProp, BS_W + bands.left);
  const marks = useChartMarks<string>();
  const W = outerW - bands.left;
  const compact = outerW < COMPACT_W;
  const values = bars
    .map((b) => b.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (typeof mean === 'number' && Number.isFinite(mean)) values.push(mean);
  // Bars grow from a zero baseline: a cropland share or a rainfall total is a quantity, and
  // a chart that cut the axis would exaggerate every year-to-year wobble the client's
  // captions are deliberately cautious about.
  const hi = values.length ? Math.max(...values, 0) : 1;
  const span = hi || 1;
  const n = Math.max(bars.length, 1);
  const slot = (W - 2 * BS_PAD_X) / n;
  const barW = Math.max(3, Math.min(slot * 0.68, 48));
  const baseY = BS_PAD_TOP + BS_PLOT_H;
  const stride = labelStride(bars.map((b) => b.label), slot, compact);

  const readout =
    `${ariaLabel}: ` +
    bars.map((b) => `${b.label} ${valueText(b.value, unit, decimals)}`).join('. ') +
    (typeof mean === 'number'
      ? `. ${meanLabel ?? 'mean'} ${valueText(mean, unit, decimals)}`
      : '');
  const meanY =
    typeof mean === 'number' && Number.isFinite(mean)
      ? baseY - (mean / span) * BS_PLOT_H
      : null;

  return (
    <svg
      ref={svgRef}
      className={`intel-chart bar-series${marks.rootClass}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${BS_H + bands.bottom}`}
      role="img"
      aria-label={readout}
      data-testid={testId}
      data-fluid=""
      data-w={outerW}
      data-compact={compact ? '' : undefined}
      onMouseLeave={marks.clear}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + BS_PAD_X}
        plotRight={bands.left + W - BS_PAD_X}
        plotTop={BS_PAD_TOP}
        plotBottom={BS_PAD_TOP + BS_PLOT_H}
        height={BS_H + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined} role="list">
      {/* The mean line first, beneath the bars and every label (launch round, 7 Oct 2026). */}
      {meanY != null ? (
        <line
          data-mean=""
          x1={BS_PAD_X}
          x2={W - BS_PAD_X}
          y1={meanY}
          y2={meanY}
          stroke={MEAN_COLOR}
          strokeWidth={1.5}
          strokeDasharray="3,2"
        />
      ) : null}
      {bars.map((bar, i) => {
        const x = BS_PAD_X + i * slot + (slot - barW) / 2;
        const h =
          bar.value == null || !Number.isFinite(bar.value)
            ? 0
            : Math.max(1, (Math.max(bar.value, 0) / span) * BS_PLOT_H);
        const showLabel = showsLabel(i, bars.length, stride);
        const meanRow =
          typeof mean === 'number' && Number.isFinite(mean)
            ? [{ label: meanLabel ?? 'mean', value: valueText(mean, unit, decimals) }]
            : undefined;
        return (
          <g
            key={bar.id}
            className={`bs-bar mark${marks.isHot(bar.id) ? ' is-hot' : ''}`}
            data-bar={bar.id}
            {...marks.markProps(bar.id, () => ({
              label: bar.label,
              value: valueText(bar.value, unit, decimals),
              rows: meanRow,
            }))}
          >
            <title>{`${bar.label} · ${valueText(bar.value, unit, decimals)}`}</title>
            {h > 0 ? (
              <rect
                className="mk"
                x={x}
                y={baseY - h}
                width={barW}
                height={h}
                rx={2}
                fill={bar.color ?? BAR_COLOR}
                opacity={bar.highlight === false ? 0.45 : 1}
              />
            ) : null}
            <text
              className="bs-label"
              x={x + barW / 2}
              y={baseY + BS_LABEL_H - 4}
              textAnchor={i === 0 && slot < 40 ? 'start' : i === bars.length - 1 && slot < 40 && bars.length > 1 ? 'end' : 'middle'}
              fontSize={ROW_LABEL_PX}
              visibility={showLabel ? undefined : 'hidden'}
              data-secondary={showLabel ? undefined : ''}
            >
              {bar.label}
            </text>
          </g>
        );
      })}
      {/* Its words last, on the panel's halo, so no bar covers them. */}
      {meanY != null && meanLabel ? (
        <text
          className="bs-meanlabel"
          x={W - BS_PAD_X}
          y={Math.max(9, meanY - 3)}
          textAnchor="end"
          fontSize="10"
          fill={MEAN_COLOR}
          style={VALUE_HALO}
          pointerEvents="none"
        >
          {meanLabel}
        </text>
      ) : null}
      </g>
    </svg>
  );
}

export default BarSeries;
